import React, {
    useState, useEffect, useCallback, useMemo, Fragment,
} from 'react';
import { createPortal } from 'react-dom';
import Tippy from '@tippyjs/react';
import { Link } from '@reach/router';
import $ from 'jquery';
import { PAGE_VISIBILITY } from '../../utilities/CONSTANTS';
import {
    getApiPageQuestionUrl,
    getGeneratedQuestionUrl,
    getApiPageGenerateQuestionsUrl,
    getQuestionUrl,
} from '../../utilities/urlHelper';
import { hasKaiFeature } from '../../utilities/featureHelper';

const KaiQuestionDeleteButton = ({ kaiQuestionId, removeKaiQuestionById }) => (
    <Tippy content="Remove this question as it's not relevant" placement="left">
        <button
            className="page-insight-delete"
            onClick={() => removeKaiQuestionById(kaiQuestionId)}
            aria-label="remove this question as it is not relevant"
            type="button"
        >
            &times;
        </button>
    </Tippy>
);

const KaiQuestionListElement = ({ kaiQuestion, removeKaiQuestionById }) => {
    const { id, text } = kaiQuestion;
    const kaiQuestionUrl = getGeneratedQuestionUrl(id);

    return (
        <li className="page-insight-item">
            <a className="flex-auto" href={kaiQuestionUrl}>{text}</a>
            <KaiQuestionDeleteButton kaiQuestionId={id} removeKaiQuestionById={removeKaiQuestionById} />
        </li>
    );
};

const TeamQuestionListElement = ({ teamQuestion }) => {
    const { id, title } = teamQuestion;
    const teamQuestionUrl = getQuestionUrl(id);

    return (
        <li className="page-insight-item">
            <a className="flex-auto" href={teamQuestionUrl}>{title}</a>
        </li>
    );
};

const TabBar = ({ selectedTab, kaiQuestionCount = '', teamQuestionCount = '' }) => (

    <div className="tab-nav mb2">
        <Link to="?questions-tab=team">
            <div
                className={`tab b ${selectedTab === 'team' ? 'active' : ''}`}
                type="button"
            >
                    By your Team
                <span className="tab-nav-count">{teamQuestionCount}</span>
            </div>
        </Link>
        {hasKaiFeature() && (
            <Link to="?questions-tab=kai">
                <div
                    className={`tab a ${selectedTab === 'kai' ? 'active' : ''}`}
                    type="button"
                >
                    By Kai
                    <span className="tab-nav-count">{kaiQuestionCount}</span>
                </div>
            </Link>
        )}
    </div>
);

const GenerateQuestionsSection = ({ pageId, pageContentLength, isRegenerate }) => {
    const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
    const generateQuestionsBtnText = isRegenerate ? 'Generate different FAQs' : 'Generate FAQs';

    const generateQuestions = () => {
        setIsGeneratingQuestions(true);
        const url = getApiPageGenerateQuestionsUrl(pageId);
        $.ajax({
            type: 'POST',
            url,
            data: { isRegenerate },
        }).catch(console.error);
    };

    if (isGeneratingQuestions) {
        return (
            <p className="m0">FAQs are being generated. If you have Slack notifications enabled, we will notify you when this is complete.</p>
        );
    }

    return (
        <>
            { !isRegenerate && pageContentLength > 0 && pageContentLength < window._min_word_classification
                && <p className="m0 mb2">I didn't generate FAQs for this page based on its content because it's so short. Should I do that now?</p>}

            { !isRegenerate && pageContentLength > 0 && pageContentLength >= window._min_word_classification
                && <p className="m0 mb2">I haven't generated FAQs for this page yet. Should I do that now?</p>}

            { !isRegenerate && pageContentLength === 0
                && <p className="m0 mb2">I need some content before I can generate FAQs.</p>}

            { isRegenerate && <p className="m0 mb2">Here are the FAQs I generated based on the content of this page.</p>}

            { pageContentLength > 0
                && (
                    <button
                        className="btn btn-primary"
                        onClick={generateQuestions}
                        type="button"
                    >
                        {generateQuestionsBtnText}
                    </button>
                )}
        </>
    );
};

const PageGeneratedQuestionsModal = ({
    questions, answeredQuestions, pageId, pageVisibility, pageContentLength, closeModal, updateQuestions,
}) => {
    const [kaiQuestionList, setKaiQuestionList] = useState(questions || []);
    const [teamQuestions] = useState(answeredQuestions || []);
    const [selectedTab, setSelectedTab] = useState('kai');
    const [isPrivatePage] = useState(pageVisibility === PAGE_VISIBILITY.PRIVATE);

    if (kaiQuestionList.length) {
        window.localStorage.removeItem(`isGeneratingQuestions-${pageId}`);
    }

    const removeKaiQuestionById = useCallback((id) => {
        const url = getApiPageQuestionUrl(pageId, id);
        $.ajax({
            type: 'DELETE',
            url,
        }).catch(console.error);

        const updatedKaiQuestions = kaiQuestionList.filter(question => (question.id !== id));
        setKaiQuestionList(updatedKaiQuestions);
        updateQuestions(updatedKaiQuestions);
    }, [kaiQuestionList, pageId, updateQuestions]);

    const kaiQuestionListElements = useMemo(
        () => kaiQuestionList.map(question => (
            <KaiQuestionListElement
                key={question.id}
                kaiQuestion={question}
                removeKaiQuestionById={removeKaiQuestionById}
            />
        )),
        [kaiQuestionList, removeKaiQuestionById],
    );

    const teamQuestionListElements = useMemo(
        () => teamQuestions.map(question => (
            <TeamQuestionListElement
                key={question.id}
                teamQuestion={question}
            />
        )),
        [teamQuestions],
    );

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const tab = params.get('questions-tab');
        setSelectedTab(tab === 'kai' ? 'kai' : 'team');
    }, [window.location.search]);

    const onClickModal = useCallback(({ target }) => {
        const className = target.getAttribute('class');
        if (closeModal && className && className.includes('react-modal open')) {
            closeModal();
        }
    }, [closeModal]);

    return createPortal(
        <div className="react-modal open" id="page-insights-modal" onMouseDown={onClickModal}>
            <div className="modal-content p2">
                <h2>Questions</h2>
                <p className="gray">Here are the questions this page has been used to answer</p>
                <TabBar selectedTab={selectedTab} kaiQuestionCount={kaiQuestionList.length} teamQuestionCount={teamQuestions.length} />
                {selectedTab === 'kai' && hasKaiFeature()
                    ? (
                        <>
                            <div className="bg-light-blue border border-blue rounded p2 mb2">
                                <div className="flex">
                                    <div className="flex-none mr2 sm-show">
                                        <a href={window.support_link_ai_features} target="_blank" rel="noopener noreferrer">
                                            <Tippy content="Kai" placement="bottom">
                                                <img
                                                    className="bot-avatar"
                                                    src="/img/kai.png"
                                                    width="28"
                                                    height="28"
                                                    alt="Kai"
                                                    title="Kai"
                                                />
                                            </Tippy>
                                        </a>

                                    </div>
                                    <div className="flex-auto">
                                        <div>
                                            <span className="bold">Kai</span>
                                            <span className="ml1 tag tag-small navy bg-blue align-text-bottom">AI</span>
                                        </div>
                                        {(kaiQuestionList.length === 0)
                                            ? isPrivatePage
                                                ? <p className="m0">I can't generate questions for this page because it's in a private category.</p>
                                                : <GenerateQuestionsSection pageId={pageId} pageContentLength={pageContentLength} isRegenerate={false} />
                                            : (
                                                <GenerateQuestionsSection pageId={pageId} pageContentLength={pageContentLength} isRegenerate />
                                            )}
                                    </div>
                                </div>
                            </div>
                            {(!isPrivatePage && (kaiQuestionList.length > 0))
                        && (
                            <ul className="page-insights-list" id="page-questions-list">
                                {kaiQuestionListElements && kaiQuestionListElements.length > 0
                                    ? kaiQuestionListElements
                                    : ''}
                            </ul>
                        )}
                        </>
                    )
                    : (
                        <ul className="page-insights-list" id="page-answered-questions-list">
                            {teamQuestionListElements && teamQuestionListElements.length > 0
                                ? teamQuestionListElements
                                : 'No answered questions found.'}
                        </ul>
                    )}
                <div className="right-align">
                    <span className="btn-secondary mr1" onClick={closeModal}>Close</span>
                </div>
            </div>
        </div>,
        document.body,
    );
};

export default PageGeneratedQuestionsModal;
